import {
  deleteField,
  DocumentData,
  FieldValue,
  setDoc,
  Transaction,
  UpdateData,
  updateDoc
} from 'firebase/firestore';

import { BaseDao } from './base-dao';
import {
  cleanupEntityEventChangesForDeletedEntity,
  getEventEntityCleanup
} from './event-reference-cleanup';
import {
  getLocationReferenceCleanup,
  relationshipReferencesLocation
} from './location-reference-cleanup';

import type { Character, Event, EventEntityChange, Location, Novel, Organization, Relationship } from '@/lib/types';
import { normalizeImageWrite } from '@/lib/utils/image-identity';

/**
 * Data Access Object for Worldbuilding Entities (Characters, Locations, etc.).
 * Handles automatic summary mirroring to maintain consistency.
 */
export class EntityDao extends BaseDao {
  /**
   * Fields that are automatically mirrored from the full document to the metadata summary.
   */
  private readonly SUMMARY_FIELDS = [
    'id', 'name', 'fullName', 'nickname', 'type', 'shortDescription', 'mainImageUrl', 'mainImagePath', 'mapImageUrl', 'mapImageStoragePath', 'parentId', 'updatedAt', 'order', 'date',
    'characterIds', 'organizationIds', 'locationIds', 'graphPosition', 'graphPositionUpdatedBy',
    'graphPositionUpdatedAt', 'primaryImageId'
  ];

  /**
   * Fields that do not trigger an updatedAt bump when modified alone.
   */
  private readonly SILENT_FIELDS = [
    'graphPosition',
    'graphPositionUpdatedAt',
    'graphPositionUpdatedBy'
  ];

  /**
   * Updates an entity and automatically syncs changes to the summary.
   */
  async updateEntity<T extends { id: string }>(
    userId: string,
    novel: Novel,
    type: string,
    entityId: string,
    updates: UpdateData<DocumentData> | FieldValue,
    transaction?: Transaction
  ): Promise<void> {
    const updateKeys = updates && typeof updates === 'object' && !('isEqual' in updates)
      ? Object.keys(updates)
      : [];

    const isSilentUpdate = updateKeys.length > 0 && updateKeys.every(key => this.SILENT_FIELDS.includes(key));

    // 1. Update Full Item
    const updateRecord = updates && typeof updates === 'object' && !('isEqual' in updates)
      ? (updates as Record<string, unknown>)
      : {};
    const normalizedRecord = normalizeImageWrite(updateRecord);
    const finalUpdates = isSilentUpdate
      ? { ...updates }
      : { ...normalizedRecord, updatedAt: new Date().toISOString() };

    await this.updateItem(userId, novel, 'entities', type, entityId, finalUpdates, transaction);

    // 2. Automagically Update Summary
    const summaryUpdates: Record<string, unknown> = {};
    Object.entries(finalUpdates).forEach(([key, value]) => {
      if (this.SUMMARY_FIELDS.includes(key)) {
        summaryUpdates[key] = value;
      }
    });

    if (Object.keys(summaryUpdates).length > 0) {
      const { ref, fieldPath } = this.resolver.resolveSummary(userId, novel.id, 'entities', type, entityId, novel.storageLayout);
      if (fieldPath) {
        const payload: Record<string, unknown> = {};
        Object.entries(summaryUpdates).forEach(([key, value]) => {
          payload[`${fieldPath}.${key}`] = value;
        });

        if (transaction) {
          transaction.update(ref, payload as UpdateData<DocumentData>);
        } else {
          try {
            await updateDoc(ref, payload as UpdateData<DocumentData>);
          } catch (e) {
            try {
              const nested = this.nestPayload(fieldPath, summaryUpdates);
              await setDoc(ref, nested, { merge: true });
            } catch (setErr) {
              console.warn(`Summary update failed for ${entityId}: Document might not exist.`, setErr);
            }
          }
        }
      }
    }
  }

  /**
   * Removes every reference that must not survive a location deletion.
   */
  private async deleteLocationReferences(
    userId: string,
    novel: Novel,
    locationId: string
  ): Promise<void> {
    const [relationships, locations] = await Promise.all([
      this.getItems<Relationship>(userId, novel, 'entities', 'relationships'),
      this.getItems<Location>(userId, novel, 'entities', 'locations'),
    ]);

    const relationshipDeletions = relationships
      .filter((relationship) => relationshipReferencesLocation(relationship, locationId))
      .map((relationship) => (
        this.deleteItem(userId, novel, 'entities', 'relationships', relationship.id)
      ));

    const locationUpdates = locations.flatMap((location) => {
      if (location.id === locationId) return [];

      const cleanup = getLocationReferenceCleanup(location, locationId);
      if (!cleanup) return [];

      const updates: UpdateData<DocumentData> = { ...cleanup.updates };
      if (cleanup.clearParent) {
        updates.parentId = deleteField();
        updates.parentName = deleteField();
      }

      return [this.updateEntity(userId, novel, 'locations', location.id, updates)];
    });

    await Promise.all([...relationshipDeletions, ...locationUpdates]);
  }

  /**
   * Removes references to a deleted entity from all events in the novel and deletes
   * associated eventChanges.
   */
  private async deleteEventEntityReferences(
    userId: string,
    novel: Novel,
    entityId: string,
    transaction?: Transaction
  ): Promise<void> {
    const [events, characters, organizations, locations] = await Promise.all([
      this.getItems<Event>(userId, novel, 'entities', 'events'),
      this.getItems<Character>(userId, novel, 'entities', 'characters'),
      this.getItems<Organization>(userId, novel, 'entities', 'organizations'),
      this.getItems<Location>(userId, novel, 'entities', 'locations'),
    ]);

    const entityGroupList: Array<{ type: string; items: (Character | Organization | Location)[] }> = [
      { type: 'characters', items: characters },
      { type: 'organizations', items: organizations },
      { type: 'locations', items: locations },
    ];

    const entityUpdatePromises: Promise<void>[] = [];
    const eventChangesToRemoveByEventId = new Map<string, Set<string>>();

    for (const group of entityGroupList) {
      for (const item of group.items) {
        if (item.id === entityId) continue;
        const cleanup = cleanupEntityEventChangesForDeletedEntity(item.eventChanges, entityId);
        if (cleanup) {
          entityUpdatePromises.push(
            this.updateEntity(userId, novel, group.type, item.id, { eventChanges: cleanup.updatedChanges }, transaction)
          );
          for (const affEventId of cleanup.affectedEventIds) {
            if (!eventChangesToRemoveByEventId.has(affEventId)) {
              eventChangesToRemoveByEventId.set(affEventId, new Set());
            }
            eventChangesToRemoveByEventId.get(affEventId)?.add(item.id);
          }
        }
      }
    }

    const eventUpdatePromises = events.flatMap((event) => {
      const updates = getEventEntityCleanup(event, entityId) || {};
      const affectedEntities = eventChangesToRemoveByEventId.get(event.id);

      if (affectedEntities && Array.isArray(event.eventChanges)) {
        const currentChanges = (updates.eventChanges as EventEntityChange[]) || event.eventChanges;
        const filteredChanges = currentChanges.filter((change: EventEntityChange) => !affectedEntities.has(change.entityId));
        if (filteredChanges.length !== currentChanges.length) {
          updates.eventChanges = filteredChanges;
        }
      }

      if (Object.keys(updates).length === 0) return [];
      return [this.updateEntity(userId, novel, 'events', event.id, updates, transaction)];
    });

    await Promise.all([...entityUpdatePromises, ...eventUpdatePromises]);
  }

  /**
   * Deletes an entity and its summary. Location deletion also removes its
   * relationships and references from other locations. Unlinks deleted entities
   * from any associated events and removes their eventChanges.
   */
  async deleteEntity(
    userId: string,
    novel: Novel,
    type: string,
    entityId: string,
    transaction?: Transaction
  ): Promise<void> {
    if (type === 'locations') {
      if (transaction) {
        throw new Error('Location cascade deletion does not support an external transaction.');
      }
      await this.deleteLocationReferences(userId, novel, entityId);
    }

    if (type !== 'events') {
      await this.deleteEventEntityReferences(userId, novel, entityId, transaction);
    }

    // 1. Delete Full Item
    await this.deleteItem(userId, novel, 'entities', type, entityId, transaction);

    // 2. Delete Summary
    const { ref, fieldPath } = this.resolver.resolveSummary(userId, novel.id, 'entities', type, entityId, novel.storageLayout);
    if (fieldPath) {
      const updates = { [fieldPath]: deleteField() };
      if (transaction) {
        transaction.update(ref, updates);
      } else {
        await updateDoc(ref, updates);
      }
    }
  }

  /**
   * Bulk saving entities with summary mirroring.
   */
  async saveEntity<T extends { id: string }>(
    userId: string,
    novel: Novel,
    type: string,
    entity: T,
    transaction?: Transaction
  ): Promise<void> {
    // 1. Save Full Item
    const now = new Date().toISOString();
    const entityRecord = entity as Record<string, unknown>;
    const existingAudit = (entityRecord.audit && typeof entityRecord.audit === 'object')
      ? (entityRecord.audit as Record<string, unknown>)
      : {};
    const audit = {
      createdAt: (typeof existingAudit.createdAt === 'string' && existingAudit.createdAt) || now,
      updatedAt: now,
    };
    const finalItem = normalizeImageWrite({ ...entity, audit, updatedAt: now });
    await this.setItem(userId, novel, 'entities', type, finalItem, transaction);

    // 2. Save Summary
    const summary: Record<string, unknown> = {};
    Object.entries(finalItem).forEach(([key, value]) => {
      if (this.SUMMARY_FIELDS.includes(key)) {
        summary[key] = value;
      }
    });

    const { ref, fieldPath } = this.resolver.resolveSummary(userId, novel.id, 'entities', type, entity.id, novel.storageLayout);
    if (fieldPath) {
      const updates = { [fieldPath]: summary };
      if (transaction) {
        transaction.update(ref, updates);
      } else {
        try {
          await updateDoc(ref, updates);
        } catch (e) {
          try {
            const nested = this.nestPayload(fieldPath, summary);
            await setDoc(ref, nested, { merge: true });
          } catch (setErr) {
            console.warn(`Summary save failed for ${entity.id}: Document might not exist.`, setErr);
          }
        }
      }
    }
  }
}
